import { useEffect, useMemo, useState } from 'react';
import { Droplets, Sword, Shield, Gem, RotateCcw, Check, ChevronRight, Sparkles, Hand, Crosshair } from 'lucide-react';
import { toast } from 'sonner';
import { InkDot } from '@/components/InkDot';
import type { InkColor } from '@/lib/ink-utils';
import { useLearnProgress } from '@/hooks/useLearnProgress';

/* ─────────────────────────────────────────────────────────────
   Scripted, deterministic first-turn scenario.
   We model a tiny subset of state — just enough to teach the
   core verbs: INK → PLAY → QUEST → CHALLENGE.
   ───────────────────────────────────────────────────────────── */

type SimCard = {
  id: string;
  name: string;
  ink: InkColor;
  cost: number;
  type: 'character' | 'action';
  strength?: number;
  willpower?: number;
  lore?: number;
  inkable: boolean;
  /** rules-text shown on the card */
  text?: string;
};

const HAND: SimCard[] = [
  { id: 'mickey', name: 'Mickey Mouse', ink: 'Ruby', cost: 2, type: 'character', strength: 2, willpower: 3, lore: 1, inkable: true, text: 'A friendly 2-cost quester.' },
  { id: 'sword',  name: 'Magic Brooms', ink: 'Amber', cost: 3, type: 'character', strength: 1, willpower: 2, lore: 2, inkable: true, text: 'Used later. For now, ink it.' },
  { id: 'lantern', name: 'Lantern Light',  ink: 'Amber', cost: 1, type: 'action', inkable: false, text: 'Non-inkable (no silver swirl).' },
];

const OPPONENT_CHARACTER: SimCard = {
  id: 'opp-bandit',
  name: 'Stromboli',
  ink: 'Amethyst',
  cost: 3,
  type: 'character',
  strength: 2,
  willpower: 3,
  lore: 1,
  inkable: true,
  text: 'Currently exerted — vulnerable to a challenge.',
};

type Step =
  | 'intro'
  | 'ink'
  | 'play'
  | 'wait-turn'
  | 'quest'
  | 'challenge'
  | 'done';

type CoachCopy = {
  title: string;
  body: string;
  why: string;
};

const COACH: Record<Step, CoachCopy> = {
  intro: {
    title: 'Welcome to your first turn',
    body: 'You have a 3-card hand and 0 ink. The goal: ink a card, play a character, then next turn quest for lore.',
    why: 'Every turn in Lorcana follows: BEGINNING → DRAW (skipped turn 1) → MAIN PHASE → END.',
  },
  ink: {
    title: 'Step 1 — Put a card into your inkwell',
    body: 'Tap a card with the silver swirl border to ink it. You may ink at most one card per turn.',
    why: 'Ink is your resource. Without it, you can\'t play anything.',
  },
  play: {
    title: 'Step 2 — Play a character',
    body: 'Tap Mickey Mouse (2 cost) — but you only have 1 ink right now. We\'ll skip to turn 2 where you have enough.',
    why: 'Characters enter exerted (tapped). They can\'t quest or challenge the turn they\'re played.',
  },
  'wait-turn': {
    title: 'Opponent\'s turn passes',
    body: 'Your opponent plays Stromboli and ends exerted. It\'s now your turn 3 — Mickey is ready.',
    why: 'Use the time between turns to plan: who quests, who blocks, who challenges?',
  },
  quest: {
    title: 'Step 3 — Quest with Mickey',
    body: 'Tap Mickey to quest. You\'ll exert him and earn lore equal to his lore value.',
    why: 'Lore is how you win — first to 20. Quest whenever it\'s safe.',
  },
  challenge: {
    title: 'Step 4 — Challenge the exerted Stromboli',
    body: 'You have another ready character (we\'ll pretend Magic Brooms is now in play). Tap Stromboli to challenge.',
    why: 'Challenging exerted characters is how you remove threats. Both deal strength damage at once.',
  },
  done: {
    title: 'Turn complete!',
    body: 'You inked, played, quested for lore, and challenged. That\'s the entire core loop of Lorcana.',
    why: 'Repeat this loop while watching your opponent\'s board state, lore, and ink each turn.',
  },
};

const STEP_ORDER: Step[] = ['intro', 'ink', 'play', 'wait-turn', 'quest', 'challenge', 'done'];
const LESSON_KEY = 'sim-first-turn';

export function TurnSimulator() {
  const { isComplete, toggle } = useLearnProgress();
  const [step, setStep] = useState<Step>('intro');
  const [hand, setHand] = useState<SimCard[]>(HAND);
  const [inkwell, setInkwell] = useState<SimCard[]>([]);
  const [exertedInk, setExertedInk] = useState(0);
  const [board, setBoard] = useState<{ card: SimCard; exerted: boolean }[]>([]);
  const [oppBoard, setOppBoard] = useState<{ card: SimCard; exerted: boolean; damage: number } | null>(null);
  const [yourLore, setYourLore] = useState(0);
  const [oppLore] = useState(0);
  const [log, setLog] = useState<string[]>([]);

  const stepIndex = STEP_ORDER.indexOf(step);

  const push = (s: string) => setLog((l) => [...l, s]);

  // Auto-mark complete the first time you reach `done`
  useEffect(() => {
    if (step === 'done' && !isComplete(LESSON_KEY)) {
      toggle(LESSON_KEY);
      toast.success('Sample turn complete — added to your Learn progress!');
    }
  }, [step, isComplete, toggle]);

  const reset = () => {
    setStep('intro');
    setHand(HAND);
    setInkwell([]);
    setExertedInk(0);
    setBoard([]);
    setOppBoard(null);
    setYourLore(0);
    setLog([]);
  };

  // ─── Actions ────────────────────────────────────────────────
  const startScenario = () => setStep('ink');

  const onInkCard = (c: SimCard) => {
    if (step !== 'ink') return;
    if (!c.inkable) {
      toast.error('That card is non-inkable (no silver swirl).');
      return;
    }
    setHand((h) => h.filter((x) => x.id !== c.id));
    setInkwell((i) => [...i, c]);
    push(`Inked ${c.name}.`);
    // Move to "play" step but bootstrap turn 2 (give them 2 ink and Mickey to play)
    setTimeout(() => {
      // Advance to turn 2 — refill ink + add a ready ink
      setInkwell((curr) => [...curr, { ...c, id: c.id + '-t2', name: c.name + ' (turn 2)' }]);
      setStep('play');
    }, 350);
  };

  const onPlayCard = (c: SimCard) => {
    if (step !== 'play') return;
    if (c.id !== 'mickey') {
      toast.error('Play Mickey Mouse (2 cost).');
      return;
    }
    if (inkwell.length - exertedInk < c.cost) {
      toast.error('Not enough ready ink.');
      return;
    }
    setExertedInk((n) => n + c.cost);
    setHand((h) => h.filter((x) => x.id !== c.id));
    setBoard((b) => [...b, { card: c, exerted: true }]);
    push(`Played ${c.name} (cost ${c.cost}). Enters exerted.`);
    setTimeout(() => setStep('wait-turn'), 400);
  };

  const advanceFromWait = () => {
    // Opponent plays Stromboli exerted; your characters ready up; refresh ink; add Magic Brooms in play.
    setBoard((b) =>
      b
        .map((row) => ({ ...row, exerted: false }))
        // pretend Magic Brooms has been played and is ready
        .concat({ card: HAND[1], exerted: false }),
    );
    setExertedInk(0);
    setOppBoard({ card: OPPONENT_CHARACTER, exerted: true, damage: 0 });
    push("Opponent played Stromboli (exerted). Your turn 3 — characters ready.");
    setStep('quest');
  };

  const onQuestCharacter = (c: SimCard) => {
    if (step !== 'quest') return;
    if (c.id !== 'mickey') {
      toast.error('Quest with Mickey first — he has 1 lore.');
      return;
    }
    setBoard((b) => b.map((row) => (row.card.id === c.id ? { ...row, exerted: true } : row)));
    setYourLore((l) => l + (c.lore ?? 0));
    push(`${c.name} questing — +${c.lore} lore.`);
    setTimeout(() => setStep('challenge'), 400);
  };

  const onChallengeOpp = () => {
    if (step !== 'challenge' || !oppBoard) return;
    // Use the ready Magic Brooms to challenge
    const attacker = board.find((b) => b.card.id === 'sword' && !b.exerted);
    if (!attacker) {
      toast.error('No ready character to challenge with.');
      return;
    }
    const attackerStr = attacker.card.strength ?? 0;
    const defenderStr = oppBoard.card.strength ?? 0;
    const defenderWp = oppBoard.card.willpower ?? 0;
    const attackerWp = attacker.card.willpower ?? 0;
    const newDefenderDmg = oppBoard.damage + attackerStr;
    const attackerDmg = defenderStr;
    push(
      `${attacker.card.name} challenges ${oppBoard.card.name} — ${attackerStr} vs ${defenderStr} damage.`,
    );
    setBoard((b) =>
      b
        .map((row) =>
          row.card.id === attacker.card.id ? { ...row, exerted: true } : row,
        )
        .filter((row) => (row.card.willpower ?? 0) > attackerDmg || row.card.id !== attacker.card.id),
    );
    if (newDefenderDmg >= defenderWp) {
      setOppBoard(null);
      push(`${oppBoard.card.name} banished!`);
    } else {
      setOppBoard({ ...oppBoard, damage: newDefenderDmg });
    }
    if (attackerDmg >= attackerWp) {
      push(`${attacker.card.name} was banished trading.`);
    }
    setTimeout(() => setStep('done'), 500);
  };

  const coach = COACH[step];

  // ─── Render helpers ─────────────────────────────────────────
  const CardChip = ({
    c,
    exerted,
    damage,
    onClick,
    actionable,
    title,
  }: {
    c: SimCard;
    exerted?: boolean;
    damage?: number;
    onClick?: () => void;
    actionable?: boolean;
    title?: string;
  }) => {
    const wp = c.willpower ?? 0;
    const overWp = damage != null && damage >= wp;
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        title={title}
        className={`relative w-[78px] shrink-0 rounded-md border text-left p-1.5 transition-all ${
          actionable
            ? 'border-primary bg-primary/10 hover:bg-primary/20 ring-1 ring-primary animate-pulse'
            : 'border-border bg-card'
        } ${exerted ? 'rotate-6 opacity-80' : ''} ${overWp ? 'opacity-40 line-through' : ''}`}
      >
        <div className="flex items-center gap-1">
          <InkDot ink={c.ink} size="sm" />
          <span className="text-[9px] font-medium text-foreground font-body truncate flex-1">{c.name}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-[9px] text-muted-foreground font-body">
          <span className="inline-flex items-center gap-0.5"><Droplets className="h-2.5 w-2.5" />{c.cost}</span>
          {c.type === 'character' && (
            <>
              <span className="inline-flex items-center gap-0.5"><Sword className="h-2.5 w-2.5" />{c.strength}</span>
              <span className="inline-flex items-center gap-0.5"><Shield className="h-2.5 w-2.5" />{c.willpower}</span>
              <span className="inline-flex items-center gap-0.5 text-ink-amber"><Gem className="h-2.5 w-2.5" />{c.lore}</span>
            </>
          )}
        </div>
        {!c.inkable && (
          <span className="absolute top-0.5 right-0.5 text-[7px] uppercase tracking-wider text-ink-ruby font-body">non-ink</span>
        )}
        {damage != null && damage > 0 && (
          <span className="absolute bottom-0.5 right-0.5 rounded bg-ink-ruby/30 px-1 text-[8px] text-ink-ruby font-body">{damage} dmg</span>
        )}
      </button>
    );
  };

  const actionableHandId = useMemo(() => {
    if (step === 'ink') return 'sword'; // best card to ink (Magic Brooms inkable)
    if (step === 'play') return 'mickey';
    return null;
  }, [step]);

  const actionableBoardId = useMemo(() => {
    if (step === 'quest') return 'mickey';
    return null;
  }, [step]);

  return (
    <div className="space-y-3">
      {/* Coach overlay */}
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground font-body">{coach.title}</h3>
          <span className="ml-auto text-[10px] text-muted-foreground font-body">
            Step {stepIndex + 1} / {STEP_ORDER.length}
          </span>
        </div>
        <p className="text-xs text-foreground/90 font-body leading-relaxed">{coach.body}</p>
        <p className="mt-1.5 text-[11px] text-muted-foreground font-body italic">Why: {coach.why}</p>
        {step === 'intro' && (
          <button
            type="button"
            onClick={startScenario}
            className="mt-2 inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90"
          >
            Start <ChevronRight className="h-3 w-3" />
          </button>
        )}
        {step === 'wait-turn' && (
          <button
            type="button"
            onClick={advanceFromWait}
            className="mt-2 inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90"
          >
            End opponent's turn <ChevronRight className="h-3 w-3" />
          </button>
        )}
        {step === 'done' && (
          <div className="mt-2 flex gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-ink-emerald/20 px-2 py-0.5 text-[10px] font-medium text-ink-emerald font-body">
              <Check className="h-3 w-3" /> Lesson complete
            </span>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium text-foreground font-body hover:bg-muted"
            >
              <RotateCcw className="h-3 w-3" /> Replay
            </button>
          </div>
        )}
      </div>

      {/* Score strip */}
      <div className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-body">You</p>
          <p className="text-lg font-display font-bold text-foreground">{yourLore}<span className="text-xs text-muted-foreground font-body"> lore</span></p>
        </div>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-body">Ink</p>
          <p className="text-sm font-body text-foreground">{inkwell.length - exertedInk} / {inkwell.length}</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-body">Opp</p>
          <p className="text-lg font-display font-bold text-foreground">{oppLore}<span className="text-xs text-muted-foreground font-body"> lore</span></p>
        </div>
      </div>

      {/* Opponent board */}
      <div className="rounded-xl border border-border bg-card p-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1.5 flex items-center gap-1">
          <Crosshair className="h-3 w-3" /> Opponent's board
        </p>
        <div className="flex gap-1.5 min-h-[60px]">
          {oppBoard ? (
            <CardChip
              c={oppBoard.card}
              exerted={oppBoard.exerted}
              damage={oppBoard.damage}
              onClick={step === 'challenge' ? onChallengeOpp : undefined}
              actionable={step === 'challenge'}
              title="Tap to challenge"
            />
          ) : (
            <p className="text-[10px] text-muted-foreground font-body italic self-center px-2">No characters in play.</p>
          )}
        </div>
      </div>

      {/* Your board */}
      <div className="rounded-xl border border-border bg-card p-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1.5">Your board</p>
        <div className="flex gap-1.5 min-h-[60px] overflow-x-auto">
          {board.length ? (
            board.map((row, i) => (
              <CardChip
                key={`${row.card.id}-${i}`}
                c={row.card}
                exerted={row.exerted}
                onClick={
                  step === 'quest' && row.card.id === actionableBoardId && !row.exerted
                    ? () => onQuestCharacter(row.card)
                    : undefined
                }
                actionable={
                  step === 'quest' && row.card.id === actionableBoardId && !row.exerted
                }
                title="Tap to quest"
              />
            ))
          ) : (
            <p className="text-[10px] text-muted-foreground font-body italic self-center px-2">Nothing in play yet.</p>
          )}
        </div>
      </div>

      {/* Inkwell */}
      <div className="rounded-xl border border-border bg-card p-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1.5 flex items-center gap-1">
          <Droplets className="h-3 w-3" /> Inkwell
        </p>
        <div className="flex gap-1 min-h-[24px]">
          {inkwell.map((c, i) => (
            <span
              key={`${c.id}-${i}`}
              className={`h-5 w-5 rounded-full border-2 ${i < exertedInk ? 'border-muted bg-muted' : 'border-primary bg-primary/30'}`}
              title={c.name}
            />
          ))}
          {!inkwell.length && (
            <p className="text-[10px] text-muted-foreground font-body italic">Empty.</p>
          )}
        </div>
      </div>

      {/* Hand */}
      <div className="rounded-xl border border-border bg-card p-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1.5 flex items-center gap-1">
          <Hand className="h-3 w-3" /> Your hand
        </p>
        <div className="flex gap-1.5 overflow-x-auto">
          {hand.map((c) => (
            <CardChip
              key={c.id}
              c={c}
              onClick={
                step === 'ink'
                  ? () => onInkCard(c)
                  : step === 'play'
                    ? () => onPlayCard(c)
                    : undefined
              }
              actionable={c.id === actionableHandId}
              title={
                step === 'ink' ? 'Tap to ink' : step === 'play' ? 'Tap to play' : undefined
              }
            />
          ))}
          {!hand.length && (
            <p className="text-[10px] text-muted-foreground font-body italic">Empty.</p>
          )}
        </div>
      </div>

      {/* Action log */}
      {log.length > 0 && (
        <details className="rounded-lg bg-secondary/40 px-3 py-2">
          <summary className="text-[10px] uppercase tracking-wider text-muted-foreground font-body cursor-pointer">Action log</summary>
          <ul className="mt-1.5 space-y-0.5">
            {log.map((entry, i) => (
              <li key={i} className="text-[11px] text-foreground/80 font-body">• {entry}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
